import "firebase/functions";
import { db, fieldValue } from "./firebase.utils";
import permissions from "../utils/permissions";

const {
  getOwnerUid,
  getWorkspaceRole,
  canManageWorkspacePerson,
  canInvite,
} = permissions;

function requireUser() {
  const uid = db.app.auth().currentUser?.uid;
  if (!uid) throw new Error("Please sign in again.");
  return uid;
}

async function readTargets(transaction, spaceId, stationId) {
  const spaceRef = db.collection("space").doc(spaceId);
  const spaceSnap = await transaction.get(spaceRef);
  if (!spaceSnap.exists) throw new Error("Workspace no longer exists.");
  const space = { ...spaceSnap.data(), spaceId };
  if (!stationId) return { spaceRef, space };
  const stationRef = db
    .collection("space")
    .doc(spaceId)
    .collection("stations")
    .doc(stationId);
  const stationSnap = await transaction.get(stationRef);
  if (!stationSnap.exists) throw new Error("Project no longer exists.");
  return {
    spaceRef,
    space,
    stationRef,
    station: { ...stationSnap.data(), stationId, fromSpaceId: spaceId },
  };
}

export async function changeWorkspaceRole(spaceId, targetUid, role) {
  const actorUid = requireUser();
  if (!["admin", "member"].includes(role))
    throw new Error("Invalid workspace role.");
  return db.runTransaction(async (transaction) => {
    const { spaceRef, space } = await readTargets(transaction, spaceId);
    if (
      getWorkspaceRole(space, actorUid) !== "owner" ||
      !canManageWorkspacePerson(space, actorUid, targetUid)
    ) {
      throw new Error("Only the owner can change workspace roles.");
    }
    const admins = (space.admins || []).filter((uid) => uid !== targetUid);
    if (role === "admin") admins.push(targetUid);
    transaction.update(spaceRef, { admins: [...new Set(admins)] });
  });
}

export async function transferWorkspaceOwnership(spaceId, targetUid) {
  const actorUid = requireUser();
  return db.runTransaction(async (transaction) => {
    const { spaceRef, space } = await readTargets(transaction, spaceId);
    if (
      getOwnerUid(space) !== actorUid ||
      actorUid === targetUid ||
      !(space.members || []).includes(targetUid)
    ) {
      throw new Error("Choose an existing workspace member as the new owner.");
    }
    transaction.update(spaceRef, {
      ownedBy: targetUid,
      admin: targetUid, // Temporary compatibility for existing owner-only screens.
      members: [...new Set([...(space.members || []), actorUid, targetUid])],
      admins: [...new Set([...(space.admins || []), actorUid])].filter(
        (uid) => uid !== targetUid,
      ),
    });
  });
}

export async function removeWorkspaceMember(spaceId, targetUid) {
  const actorUid = requireUser();
  return db.runTransaction(async (transaction) => {
    const { spaceRef, space } = await readTargets(transaction, spaceId);
    if (!canManageWorkspacePerson(space, actorUid, targetUid)) {
      throw new Error("You cannot remove this workspace member.");
    }
    transaction.update(spaceRef, {
      members: (space.members || []).filter((uid) => uid !== targetUid),
      admins: (space.admins || []).filter((uid) => uid !== targetUid),
    });
    // Explicit project memberships remain. The confirmation explains this.
  });
}

export async function changeProjectRole(
  spaceId,
  stationId,
  targetUid,
  roleKey,
) {
  requireUser();
  const result = await db.app
    .functions("us-central1")
    .httpsCallable("changeProjectMembership")({
    spaceId,
    stationId,
    targetUid,
    roleKey,
  });
  return result.data;
}
export async function removeProjectMember(spaceId, stationId, targetUid) {
  return changeProjectRole(spaceId, stationId, targetUid, null);
}

export async function createInvite({
  targetType,
  spaceId,
  stationId = null,
  inviterUid,
  inviteeUid,
  role = targetType === "station" ? "editor" : "member",
  allowChat = false,
}) {
  const actorUid = requireUser();
  if (inviterUid && inviterUid !== actorUid)
    throw new Error("Please sign in again.");
  if (
    !["space", "station"].includes(targetType) ||
    !spaceId ||
    !inviteeUid ||
    actorUid === inviteeUid ||
    (targetType === "station" && !stationId)
  ) {
    throw new Error("Missing or invalid invitation details.");
  }
  // Avoid repeated taps creating another pending invitation from this inviter.
  const pending = await db
    .collection("invites")
    .where("inviterUid", "==", actorUid)
    .where("status", "==", "pending")
    .get();
  const existing = pending.docs.find((item) => {
    const data = item.data();
    return (
      data.status === "pending" &&
      data.inviteeUid === inviteeUid &&
      data.targetType === targetType &&
      data.spaceId === spaceId &&
      (targetType === "space" || data.stationId === stationId)
    );
  });
  if (existing) return { ok: true, id: existing.id, alreadyPending: true };

  // Read fresh targets. The server rechecks authority at acceptance too.
  const spaceSnap = await db.collection("space").doc(spaceId).get();
  if (!spaceSnap.exists) throw new Error("Workspace no longer exists.");
  const space = { ...spaceSnap.data(), spaceId };
  let station = null;
  if (targetType === "station") {
    const snap = await db
      .collection("space")
      .doc(spaceId)
      .collection("stations")
      .doc(stationId)
      .get();
    if (!snap.exists) throw new Error("Project no longer exists.");
    station = { ...snap.data(), stationId, fromSpaceId: spaceId };
  }
  if (!canInvite(space, station, actorUid, targetType, role)) {
    throw new Error("You cannot send an invitation for this role.");
  }
  if (
    getWorkspaceRole(space, inviteeUid) ||
    (station?.members || []).includes(inviteeUid)
  ) {
    throw new Error(
      "This person already has access. Change their role in Members.",
    );
  }
  const ref = await db.collection("invites").add({
    targetType,
    spaceId,
    ...(targetType === "station" ? { stationId } : {}),
    inviterUid: actorUid,
    inviteeUid,
    role,
    allowChat:
      targetType === "station" && role === "viewer" && allowChat === true,
    targetName: (station || space).name || (station ? "Project" : "Workspace"),
    status: "pending",
    createdAt: fieldValue.serverTimestamp(),
    updatedAt: fieldValue.serverTimestamp(),
  });
  return { ok: true, id: ref.id, alreadyPending: false };
}

export async function respondToInvite(inviteId, status) {
  const uid = requireUser();
  if (!["accepted", "declined"].includes(status))
    throw new Error("Invalid response.");
  return db.runTransaction(async (transaction) => {
    const ref = db.collection("invites").doc(inviteId);
    const snap = await transaction.get(ref);
    if (!snap.exists || snap.data().inviteeUid !== uid)
      throw new Error("Invitation unavailable.");
    const invite = snap.data();
    if (invite.status !== "pending")
      throw new Error("This invitation has already been answered.");
    const user = await transaction.get(db.collection("users").doc(uid));
    if (
      status === "accepted" &&
      (user.data()?.blockedUsers || []).includes(invite.inviterUid)
    ) {
      throw new Error("You blocked this inviter.");
    }
    transaction.update(ref, {
      status,
      updatedAt: fieldValue.serverTimestamp(),
      ...(status === "accepted" ? { membershipStatus: "pending" } : {}),
    });
  });
}

export async function cancelInvite(inviteId) {
  const uid = requireUser();
  return db.runTransaction(async (transaction) => {
    const ref = db.collection("invites").doc(inviteId);
    const snap = await transaction.get(ref);
    if (
      !snap.exists ||
      snap.data().inviterUid !== uid ||
      snap.data().status !== "pending"
    ) {
      throw new Error("This invitation can no longer be cancelled.");
    }
    transaction.update(ref, {
      status: "cancelled",
      updatedAt: fieldValue.serverTimestamp(),
    });
  });
}

export async function blockInviteSender(inviteId) {
  const uid = requireUser();
  return db.runTransaction(async (transaction) => {
    const ref = db.collection("invites").doc(inviteId);
    const snap = await transaction.get(ref);
    if (!snap.exists || snap.data().inviteeUid !== uid)
      throw new Error("Invitation unavailable.");
    const invite = snap.data();
    transaction.set(
      db.collection("users").doc(uid),
      { blockedUsers: fieldValue.arrayUnion(invite.inviterUid) },
      { merge: true },
    );
    if (invite.status === "pending") {
      transaction.update(ref, {
        status: "declined",
        updatedAt: fieldValue.serverTimestamp(),
      });
    }
  });
}

export async function inviteByEmail({ email, ...details }) {
  const typed = email.trim();
  if (!typed) throw new Error("Enter an email address.");
  let snapshot = await db
    .collection("users")
    .where("email", "==", typed)
    .limit(2)
    .get();
  if (snapshot.empty && typed !== typed.toLowerCase()) {
    snapshot = await db
      .collection("users")
      .where("email", "==", typed.toLowerCase())
      .limit(2)
      .get();
  }
  if (snapshot.empty)
    throw new Error("No registered user has this email address.");
  if (snapshot.docs.length !== 1)
    throw new Error("More than one account matches this email.");
  const userDoc = snapshot.docs[0];
  const user = { ...userDoc.data(), uid: userDoc.id };
  const result = await createInvite({ ...details, inviteeUid: user.uid });
  return { ...result, user };
}

// One profile lookup per person per signed-in session. Chunking also supports SDK v8.
const profileCache = new Map();
export async function loadMemberProfiles(uids) {
  const actorUid = requireUser();
  const key = (uid) => `${actorUid}:${uid}`;
  const uniqueUids = [...new Set(uids.filter(Boolean))];
  const missing = uniqueUids.filter((uid) => !profileCache.has(key(uid)));
  for (let offset = 0; offset < missing.length; offset += 10) {
    const chunk = missing.slice(offset, offset + 10);
    const snapshot = await db
      .collection("users")
      .where("uid", "in", chunk)
      .get();
    snapshot.forEach((document) => {
      const user = document.data();
      if (user.uid) profileCache.set(key(user.uid), user);
    });
    // Keep a fallback so a removed account doesn't cause repeated reads.
    chunk.forEach((uid) => {
      if (!profileCache.has(key(uid)))
        profileCache.set(key(uid), { uid, userName: "Unknown member" });
    });
  }
  return Object.fromEntries(
    uniqueUids.map((uid) => [uid, profileCache.get(key(uid))]),
  );
}

// Only old invites need these target-name reads. New notifications store the names.
const targetNameCache = new Map();
export async function loadInviteLabels(invites) {
  const actorUid = requireUser();
  const users = await loadMemberProfiles(
    invites.map((invite) => invite.inviterUid),
  );
  const entries = await Promise.all(
    invites.map(async (invite) => {
      let targetName = invite.targetName;
      if (
        !targetName &&
        invite.spaceId &&
        ["space", "station"].includes(invite.targetType)
      ) {
        const path =
          invite.targetType === "space"
            ? `space/${invite.spaceId}`
            : invite.stationId
              ? `space/${invite.spaceId}/stations/${invite.stationId}`
              : null;
        if (path) {
          const key = `${actorUid}:${path}`;
          if (!targetNameCache.has(key)) {
            targetNameCache.set(
              key,
              db
                .doc(path)
                .get()
                .then((snapshot) =>
                  snapshot.exists ? snapshot.data().name || null : null,
                )
                .catch(() => {
                  targetNameCache.delete(key);
                  return null;
                }),
            );
          }
          targetName = await targetNameCache.get(key);
        }
      }
      return [
        invite.id,
        {
          targetName,
          inviterName: invite.inviterName || users[invite.inviterUid]?.userName,
        },
      ];
    }),
  );
  return Object.fromEntries(entries);
}
